import React, { useState, useEffect, useCallback } from 'react'
import { Card } from '../ui'
import { appsyncQuery } from '../api/appsync-client.js'
import { useAutoSave } from '../hooks/useAutoSave.js'

const GET_MANAGER_NOTES = `
  query GetManagerNotes($reportId: ID!, $sessionId: ID!) {
    getManagerNotes(reportId: $reportId, sessionId: $sessionId)
  }
`

const UPDATE_MANAGER_NOTES = `
  mutation UpdateManagerNotes($reportId: ID!, $sessionId: ID!, $notes: String!) {
    updateManagerNotes(reportId: $reportId, sessionId: $sessionId, notes: $notes)
  }
`

const UPDATE_SESSION_PRIVATE_NOTES = `
  mutation UpdateSession($sessionId: ID!, $input: UpdateSessionInput!) {
    updateSession(sessionId: $sessionId, input: $input) {
      sessionId privateNotes updatedAt
    }
  }
`

/**
 * Private notes card — shown to both report and manager, but each
 * party only sees their own notes. Never visible to the other side.
 *
 * - Report view (reportId == null): reads/writes session.privateNotes
 * - Manager view (reportId set):    reads/writes managerNotes via dedicated endpoint
 */
export function PrivateNotesCard({ sessionId, reportId = null, initialNotes = '', readOnly = false, onLoad, onSave: onSaveProp }) {
  const isManagerView = !!reportId
  const hasCustomCallbacks = !!onSaveProp

  const [notes, setNotes] = useState(initialNotes)
  const [loading, setLoading] = useState(hasCustomCallbacks ? false : isManagerView)

  // Manager notes are fetched separately; report notes come via the session object.
  // When custom callbacks are provided, skip the default fetch — the parent manages loading.
  useEffect(() => {
    if (hasCustomCallbacks) {
      setNotes(initialNotes)
      return
    }
    if (!isManagerView) {
      setNotes(initialNotes)
      return
    }
    setLoading(true)
    appsyncQuery(GET_MANAGER_NOTES, { reportId, sessionId })
      .then((data) => setNotes(data.getManagerNotes || ''))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [reportId, sessionId, isManagerView, initialNotes, hasCustomCallbacks])

  const handleSave = useCallback(async (_id, data) => {
    if (onSaveProp) {
      await onSaveProp(_id, data)
      return
    }
    if (isManagerView) {
      await appsyncQuery(UPDATE_MANAGER_NOTES, { reportId, sessionId, notes: data.notes })
    } else {
      await appsyncQuery(UPDATE_SESSION_PRIVATE_NOTES, { sessionId, input: { privateNotes: data.notes } })
    }
  }, [onSaveProp, isManagerView, reportId, sessionId])

  const saveState = useAutoSave(
    sessionId,
    { notes },
    handleSave,
    !loading && !readOnly,
  )

  const hint = isManagerView
    ? 'Private — not visible to your report'
    : 'Private — not visible to your manager'

  return (
    <Card
      data-testid="private-notes-card"
      style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
    >
      <div style={styles.header}>
        <span style={{ ...styles.iconBadge, background: '#88888822', color: 'var(--text-secondary)' }}>🔒</span>
        <span style={styles.label}>Private notes</span>
        <span style={styles.hint}>{hint}</span>
        {saveState === 'saving' && <span style={styles.saveIndicator}>Saving…</span>}
        {saveState === 'saved' && <span style={{ ...styles.saveIndicator, color: 'var(--energy-5)' }}>Saved</span>}
      </div>
      <div style={styles.accentBar} />
      <textarea
        style={{ ...styles.textarea, opacity: readOnly ? 0.8 : 1 }}
        value={loading ? '' : notes}
        onChange={(e) => !readOnly && setNotes(e.target.value)}
        placeholder={readOnly ? '' : 'Add private notes…'}
        disabled={loading || readOnly}
        rows={4}
        aria-label="Private notes"
      />
    </Card>
  )
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  iconBadge: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    flexShrink: 0,
  },
  label: { fontSize: '13px', fontWeight: 600, letterSpacing: '-0.01em' },
  hint: { fontSize: '11px', color: 'var(--text-secondary)', flex: 1 },
  saveIndicator: { fontSize: '11px', color: 'var(--text-secondary)' },
  accentBar: {
    height: '3px',
    opacity: 0.15,
    margin: '6px 0',
    borderRadius: '2px',
    background: 'var(--text-secondary)',
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    resize: 'vertical',
    lineHeight: '1.6',
    marginTop: '4px',
  },
}
